import { useQuery } from '@tanstack/react-query';

import { useRequest, queryClient } from '@frontend/api';
import type { QueryFunction } from '@frontend/api';

import type { DxfSceneState } from './types';

export type GetDxfSceneStatusQueryKeyType = readonly ['dxfSceneStatus', string];

export const getDxfSceneStatusQueryKey = (
  partFileUuid: string,
): GetDxfSceneStatusQueryKeyType => ['dxfSceneStatus', partFileUuid] as const;

/**
 * Fetch a part file's scene state — the same shape as `PartFile.dxfSceneState`.
 *
 * Reading this endpoint is also what *triggers* a build: the backend enqueues one
 * for a part file that has no scene, or whose scene was built by a superseded
 * schema version, and reports `building` / `regenerating` until it lands. That is
 * idempotent server-side, so a remount cannot stack up duplicate builds.
 */
export const useGetDxfSceneStatus: QueryFunction<
  DxfSceneState,
  [string | null | undefined],
  GetDxfSceneStatusQueryKeyType
> = (partFileUuid, { requestOptions, queryOptions } = {}) => {
  const queryKey = getDxfSceneStatusQueryKey(partFileUuid ?? '');
  const request = useRequest(requestOptions);
  const enabled = Boolean(partFileUuid) && (queryOptions?.enabled ?? true);

  return useQuery({
    ...queryOptions,
    enabled,
    queryKey,
    queryFn: ({ signal }) =>
      request(`/dxf/${partFileUuid}/scene`, {
        init: {
          method: 'GET',
          signal,
        },
      }),
  });
};

export const invalidateDxfSceneStatusQuery = (partFileUuid: string) =>
  queryClient.invalidateQueries({
    queryKey: getDxfSceneStatusQueryKey(partFileUuid),
  });
